import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Logo } from '@vd/ui';
import { useAuth } from '../features/auth/AuthProvider';
import { get, post, type ApiError } from '../lib/apiClient';

/**
 * The sign-in window for team mode's frame (TOADO-548).
 *
 * The frame on a customer's site opens this window, where the teammate is
 * signed in to Toado. Approving asks the API for a code it keeps under the
 * frame's PKCE challenge and leaves at the rendezvous under the frame's state;
 * the frame picks it up there. This page never sees the code, and never talks
 * to the frame: it was opened with `noopener`.
 *
 * It refuses to render inside any frame. The web app is a static site that
 * cannot send `frame-ancestors`, and an Approve button another site could
 * frame is exactly what clickjacking wants (threat model T6).
 */

const KEY_RE = /^[A-Za-z0-9_]{8,128}$/;
const CHALLENGE_RE = /^[A-Za-z0-9_-]{43,128}$/;
const STATE_RE = /^[A-Za-z0-9_-]{16,128}$/;

interface Request {
  key: string;
  site: string;
  codeChallenge: string;
  state: string;
}

interface Names {
  projectName: string;
  companyName: string;
  site: string;
}

function parse(params: URLSearchParams): Request | null {
  const request = {
    key: params.get('key') ?? '',
    site: params.get('site') ?? '',
    codeChallenge: params.get('code_challenge') ?? '',
    state: params.get('state') ?? '',
  };
  let siteIsOrigin = false;
  try { siteIsOrigin = new URL(request.site).origin === request.site; } catch { /* not a URL */ }
  return KEY_RE.test(request.key) && siteIsOrigin && CHALLENGE_RE.test(request.codeChallenge) && STATE_RE.test(request.state)
    ? request
    : null;
}

type Stage = 'loading' | 'ask' | 'approving' | 'done' | 'denied' | 'unavailable' | 'framed' | 'invalid' | 'failed';

export function WidgetSignInPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const request = useMemo(() => parse(params), [params]);
  const framed = window.top !== window.self;
  const [stage, setStage] = useState<Stage>(framed ? 'framed' : request ? 'loading' : 'invalid');
  const [names, setNames] = useState<Names | null>(null);

  useEffect(() => {
    if (framed || !request) return;
    let live = true;
    const query = new URLSearchParams({ key: request.key, site: request.site });
    get<Names>(`/v1/widget-frame/consent?${query.toString()}`)
      .then((found) => {
        if (!live) return;
        setNames(found);
        setStage('ask');
      })
      // Only a 404 means team mode is not there for this person and site; anything
      // else (a server error, a rate limit, no connection) is worth trying again.
      .catch((error: ApiError) => { if (live) setStage(error.status === 404 ? 'unavailable' : 'failed'); });
    return () => { live = false; };
  }, [framed, request]);

  const approve = async () => {
    if (!request) return;
    setStage('approving');
    try {
      await post('/v1/widget-frame/authorize', request);
      setStage('done');
      window.close();
    } catch {
      setStage('failed');
    }
  };

  const deny = () => {
    setStage('denied');
    window.close();
  };

  return (
    <div style={wrap}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Logo size={32} />
        <div>
          <h1 style={h1}>Sign in to the team panel</h1>
          {user && (
            <div style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
              Signed in to Toado as <strong>{user.email}</strong>
            </div>
          )}
        </div>
      </div>
      {stage === 'framed' && <p style={text}>This page can only be opened in its own window.</p>}
      {stage === 'invalid' && <p style={text}>This sign-in link is not complete. Start again from the site&rsquo;s team panel.</p>}
      {stage === 'loading' && <p style={text}>Checking the site&hellip;</p>}
      {stage === 'unavailable' && (
        <p style={text}>
          Team mode is not available for this site, or your account cannot see its project. Ask your workspace&rsquo;s owner.
        </p>
      )}
      {(stage === 'ask' || stage === 'approving') && names && (
        <>
          <p style={text}>
            The team panel on <strong>{names.site}</strong> is asking to act as you in{' '}
            <strong>{names.projectName}</strong> ({names.companyName}).
          </p>
          <ul style={list}>
            <li>See your team&rsquo;s pins on that site&rsquo;s pages</li>
            <li>Add pins, comment, assign, move and archive them in {names.projectName}</li>
            <li>Record walkthroughs of that site</li>
            <li>Only on {names.site}. Sign out from the panel any time.</li>
          </ul>
          <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
            <Button variant="primary" onClick={approve} disabled={stage === 'approving'}>
              {stage === 'approving' ? 'Signing in…' : 'Sign in'}
            </Button>
            <Button variant="ghost" onClick={deny} disabled={stage === 'approving'}>Cancel</Button>
          </div>
        </>
      )}
      {stage === 'done' && <p style={text}>You&rsquo;re signed in. You can close this window.</p>}
      {stage === 'denied' && <p style={text}>You didn&rsquo;t sign in. You can close this window.</p>}
      {stage === 'failed' && <p style={text}>The sign-in didn&rsquo;t go through. Close this window and try again from the site.</p>}
    </div>
  );
}

const wrap: React.CSSProperties = {
  maxWidth: 520,
  margin: '48px auto',
  padding: '28px 24px',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  fontFamily: 'Inter, sans-serif',
  color: 'var(--text)',
};
const h1: React.CSSProperties = { fontSize: 20, margin: 0 };
const text: React.CSSProperties = { color: 'var(--text-secondary)', lineHeight: 1.5 };
const list: React.CSSProperties = { color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.7, paddingLeft: 18, marginTop: 16 };
